import { useState } from "react";
import { useSubjects } from "../lib/SubjectsContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Users, Plus, Trash2, UserCircle } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/LanguageContext";
import { localizeContent } from "../lib/translations";

const EMPTY = { name: "", dob: "", age: 25, sex: "male", weight: 60, height: 165, activity: "sedang", notes: "" };

export default function SubjectSwitcher() {
  const { lang } = useI18n();
  const tr = (value) => localizeContent(value, lang);
  const { subjects, active, setActiveId, create, remove } = useSubjects();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error(tr("Nama wajib diisi"));
    setSaving(true);
    try { await create(form); toast.success(tr("Klien ditambahkan")); setForm(EMPTY); setOpen(false); }
    catch { toast.error(tr("Gagal menyimpan klien")); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm(tr("Hapus klien ini?"))) return;
    try { await remove(id); toast.success(tr("Klien dihapus")); }
    catch { toast.error(tr("Gagal menghapus")); }
  };

  return (
    <div className="flex items-center gap-2" data-testid="subject-switcher">
      <div className="flex items-center gap-2 bg-white border border-teal/20 rounded-md pl-2 pr-1 h-9">
        <UserCircle className="w-4 h-4 text-teal"/>
        <select value={active.id} onChange={(e)=>setActiveId(e.target.value)} className="text-sm bg-transparent border-0 outline-none pr-2 py-1 font-medium" data-testid="subject-select">
          {subjects.map(s => <option key={s.id} value={s.id}>{s.name}{s.id !== "__self__" ? ` · ${s.age}th` : ""}</option>)}
        </select>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline" className="rounded-md" data-testid="manage-subjects-btn"><Users className="w-4 h-4 mr-1"/> {tr("Klien")}</Button>
        </DialogTrigger>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{tr("Kelola Klien / Pasien")}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-6 mt-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-teal font-bold mb-3 flex items-center gap-2"><Plus className="w-3 h-3"/> {tr("Tambah klien baru")}</div>
              <form onSubmit={submit} className="space-y-3">
                <div><Label className="text-xs">{tr("Nama")}</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="new-subject-name"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">{tr("Usia")}</Label><Input type="number" value={form.age} onChange={(e)=>setForm({...form, age: +e.target.value})}/></div>
                  <div><Label className="text-xs">{tr("Jenis")}</Label>
                    <select value={form.sex} onChange={(e)=>setForm({...form, sex: e.target.value})} className="w-full border border-input rounded-md h-10 px-3 text-sm bg-white">
                      <option value="male">{tr("Pria")}</option><option value="female">{tr("Wanita")}</option>
                    </select>
                  </div>
                  <div><Label className="text-xs">{tr("BB (kg)")}</Label><Input type="number" value={form.weight} onChange={(e)=>setForm({...form, weight: +e.target.value})}/></div>
                  <div><Label className="text-xs">{tr("TB (cm)")}</Label><Input type="number" value={form.height} onChange={(e)=>setForm({...form, height: +e.target.value})}/></div>
                </div>
                <div><Label className="text-xs">{tr("Catatan (opsional)")}</Label>
                  <textarea value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="w-full border border-input rounded-md p-2 text-sm h-16"/>
                </div>
                <Button type="submit" disabled={saving} className="w-full rounded-md bg-teal hover:bg-teal/90 text-white" data-testid="save-subject-btn">{saving?tr("Menyimpan..."):tr("Simpan Klien")}</Button>
              </form>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-teal font-bold mb-3">{tr("Daftar klien")}</div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {subjects.filter(s => s.id !== "__self__").length === 0 ?
                  <p className="text-sm text-muted-foreground">{tr("Belum ada klien. Tambahkan di kiri.")}</p> :
                  subjects.filter(s => s.id !== "__self__").map(s => (
                    <div key={s.id} className="border border-teal/10 rounded-md p-3 flex items-start justify-between gap-2" data-testid={`subject-row-${s.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{s.name}</div>
                        <div className="text-xs text-muted-foreground">{s.age} th · {s.sex==="male"?"Pria":"Wanita"} · {s.weight}kg / {s.height}cm</div>
                      </div>
                      <button onClick={()=>del(s.id)} className="text-destructive p-1"><Trash2 className="w-4 h-4"/></button>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
